import React, { useEffect, useState } from "react";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  DialogMode,
  InfoDialog,
} from "components/shared-ui/dialogs/common/info-dialog/info-dialog";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import { commonActions } from "redux/actions/common";
import { IAppState } from "redux/reducers";
import { connect } from "react-redux";
import Button from "components/shared-ui/button";
import styles from "./recovery-codes-dialog.module.scss";
import { IRegisterMFADeviceResponse } from "api/entities/mfa";

const mapDispatchToProps = (
  dispatch: ThunkDispatch<IAppState, void, Action>,
) => {
  return {
    startLoading: () => dispatch(commonActions.startLoading()),
    stopLoading: () => dispatch(commonActions.stopLoading()),
  };
};

export interface IRecoverCodesDialogProps extends ReturnType<
  typeof mapDispatchToProps
> {
  open: boolean;
  onClose: () => void;
  recoveryCodes: IRegisterMFADeviceResponse[];
}

export const RecoveryCodesDialog = connect(
  null,
  mapDispatchToProps,
)((props: IRecoverCodesDialogProps) => {
  const [isArchiveDownloaded, setArchiveDownloaded] = useState<boolean>(false);
  const [isDownloadButtonDisabled, setDownloadButtonDisabled] =
    useState<boolean>(true);

  useEffect(() => {
    if (props.open && props.recoveryCodes && props.recoveryCodes.length > 0) {
      setDownloadButtonDisabled(false);
      setArchiveDownloaded(false);
    } else {
      setDownloadButtonDisabled(true);
      setArchiveDownloaded(false);
    }
  }, [props.open, props.recoveryCodes]);

  const getDownloadLink = () =>
    `data:application/json;charset=utf-8,${encodeURIComponent(
      JSON.stringify(props.recoveryCodes, null, 2),
    )}`;

  const downloadArchive = (fileName: string) => {
    props.startLoading();

    const link = document.createElement("a");
    link.href = getDownloadLink();
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setArchiveDownloaded(true);
    props.stopLoading();
  };

  const onDownloadBtnClick = () => {
    downloadArchive("mfa-recovery-codes.json");
  };

  const handleClose = () => {
    setArchiveDownloaded(false);
    setDownloadButtonDisabled(true);
    props.onClose();
  };

  return (
    <InfoDialog
      mode={DialogMode.Critical}
      open={props.open}
      onClose={handleClose}
      title="MFA Recovery Codes"
      showOkButton
      okCloseButtonsDisabled={
        !props.recoveryCodes ||
        props.recoveryCodes.length === 0 ||
        !isArchiveDownloaded
      }
    >
      <div className={styles.content}>
        {props.recoveryCodes && props.recoveryCodes.length > 0 ? (
          <>
            <Button
              disabled={isDownloadButtonDisabled}
              testId="download-access-codes-button"
              colors="primary"
              className={styles["btn-access-codes-download"]}
              onClick={onDownloadBtnClick}
            >
              <FileDownloadIcon />
              <span>Download</span>
            </Button>

            <div className={styles.codesDescription}>
              Please download and save these recovery codes now. You will not be
              able to view them again after closing this dialog.
            </div>

            <div className={styles["warning-message"]}>
              If you lose or forget a recovery code, you will not be able to
              recover the same code. Keep these codes in a secure location.
            </div>
          </>
        ) : (
          <div className={styles.emptyMessage}>
            No recovery codes are available at this time.
          </div>
        )}
      </div>
    </InfoDialog>
  );
});
